import { UAParser } from 'ua-parser-js'
import {
  isMobile,
  isTablet,
  browserName,
  browserVersion,
  osName,
  osVersion,
  mobileModel,
  mobileVendor
} from 'react-device-detect'
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import type { AnalyticsData } from '@/services/analytics-service'

const GEOLOCATION_REQUESTS_TIMEOUT = 3000 // Timeout for geolocation requests

// Types for geolocation data
export interface GeolocationData {
  ip?: string
  country?: string
  region?: string
  city?: string
}

// Enhanced fingerprinting using FingerprintJS
export async function generateFingerprint(): Promise<string> {
  // Initialize the agent at application startup
  const fp = await FingerprintJS.load()

  // Get the visitor identifier when you need it
  const result = await fp.get()

  // This is the visitor identifier
  return result.visitorId
}

// Enhanced device type detection using react-device-detect
export function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (isMobile) return 'mobile'
  if (isTablet) return 'tablet'
  return 'desktop'
}

// Enhanced browser and OS detection using UAParser and react-device-detect
export function getEnhancedDeviceInfo() {
  const parser = new UAParser()
  const result = parser.getResult()

  return {
    deviceType: getDeviceType(),
    browser: {
      name: browserName || result.browser.name || 'Unknown',
      version: browserVersion || result.browser.version || ''
    },
    os: {
      name: osName || result.os.name || 'Unknown',
      version: osVersion || result.os.version || ''
    },
    device: {
      model: mobileModel || result.device.model || undefined,
      vendor: mobileVendor || result.device.vendor || undefined,
      type: result.device.type || undefined
    }
  }
}

// Enhanced geolocation using browser-compatible fetch API
export async function getGeolocationDataWithCORSHandling(): Promise<GeolocationData> {
  console.log('🔄 Trying to get geolocation data...')

  try {
    // Use ipapi.co as it's CORS-enabled and free
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      signal: AbortSignal.timeout(GEOLOCATION_REQUESTS_TIMEOUT)
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()

    console.log('✅ Successfully got geolocation from ipapi.co')
    return {
      ip: data.ip,
      country: data.country_code,
      region: data.region,
      city: data.city
    }
  } catch (error) {
    console.log('⚠️ Primary geolocation service failed, trying fallback:', error)
    
    // Fallback to ip-api.com
    try {
      const fallbackResponse = await fetch('http://ip-api.com/json/', {
        method: 'GET',
        signal: AbortSignal.timeout(GEOLOCATION_REQUESTS_TIMEOUT)
      })

      if (!fallbackResponse.ok) {
        throw new Error(`HTTP error! status: ${fallbackResponse.status}`)
      }

      const fallbackData = await fallbackResponse.json()

      console.log('✅ Successfully got geolocation from fallback service')
      return {
        ip: fallbackData.query,
        country: fallbackData.countryCode,
        region: fallbackData.regionName,
        city: fallbackData.city
      }
    } catch (fallbackError) {
      console.log('⚠️ All geolocation services failed:', fallbackError)
      return {}
    }
  }
}

// Collect analytics data using enhanced detection
export async function collectAnalyticsData(): Promise<AnalyticsData> {
  const userAgent = navigator.userAgent
  const deviceInfo = getEnhancedDeviceInfo()
  const fingerprintHash = await generateFingerprint()

  return {
    session_id: crypto.randomUUID(),
    fingerprint_hash: fingerprintHash,
    user_agent: userAgent,
    referer: document.referrer || undefined,
    device_type: deviceInfo.deviceType,
    browser_name: deviceInfo.browser.name,
    browser_version: deviceInfo.browser.version,
    operating_system: deviceInfo.os.name,
    screen_resolution: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language
  }
}
